import { isFunction, sortBy } from 'lodash';

import React, {
    useState, useEffect, Fragment, memo,
} from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Highlighter from 'react-highlight-words';
import Paper from '@material-ui/core/Paper';
import MenuItem from '@material-ui/core/MenuItem';
import useStyles, { useLoadingStyle } from './SuggestionOptionsStyle';
import UtilsService from '../../../services/UtilsService';

const LoadingPreview = ({ index }) => {
    const classes = useLoadingStyle({ index });

    return <MenuItem><div className={classes.root}/></MenuItem>;
};

// Modified by AI on 05/26/2026. Edit #1.
const SuggestionOptions = ({
    testId = 'suggestion-options',
    classNames = '',
    suggestions = [],
    onSelect,
    isOpen = true,
    isFetching = false,
    isEmptyFetchResult = false,
    keyDownEvent = {},
    renderMenuItem,
    searchValueToHighlight = '',
}) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const rootClasses = UtilsService.getClassName([
        classes.rootWrapper,
        classNames,
    ]);
    const [selectedItemIndex, setSelectedItemIndex] = useState(null);

    const processKeydownEvent = () => {
        if (!keyDownEvent) {
            return null;
        }

        if (keyDownEvent.key === 'Enter' && selectedItemIndex !== null && suggestions[selectedItemIndex]) {
            return onSelect(suggestions[selectedItemIndex]);
        }

        if (keyDownEvent.key === 'ArrowUp') {
            if (selectedItemIndex === null) {
                return setSelectedItemIndex(0);
            }

            if (selectedItemIndex > 0) {
                return setSelectedItemIndex(selectedItemIndex - 1);
            }

            return setSelectedItemIndex(suggestions.length - 1);
        }

        if (keyDownEvent.key === 'ArrowDown') {
            if (selectedItemIndex === null) {
                return setSelectedItemIndex(0);
            }

            if (selectedItemIndex < suggestions.length - 1) {
                return setSelectedItemIndex(selectedItemIndex + 1);
            }

            return setSelectedItemIndex(0);
        }

        return setSelectedItemIndex(null);
    };

    useEffect(() => {
        processKeydownEvent();
    }, [keyDownEvent]);

    useEffect(() => {
        setSelectedItemIndex(null);
    }, [suggestions]);

    if (!isOpen) {
        return null;
    }

    const sortingCallBack = (suggestion) => {
        const lowerCasedLabel = (suggestion?.label || '').toLowerCase();
        const valueToSortBy = searchValueToHighlight.toLowerCase();

        return lowerCasedLabel.indexOf(valueToSortBy);
    };

    return (
        <div className={rootClasses} data-test-id={testId}>
            <Paper className={UtilsService.getClassName([classes.options, 'suggeston-options'])} square>
                {(() => {
                    if (isFetching) {
                        return (
                            <Fragment>
                                <LoadingPreview index={0}/>
                                <LoadingPreview index={1}/>
                                <LoadingPreview index={2}/>
                                <LoadingPreview index={3}/>
                                <LoadingPreview index={4}/>
                                <LoadingPreview index={5}/>
                                <LoadingPreview index={6}/>
                                <LoadingPreview index={7}/>
                            </Fragment>
                        );
                    }

                    if (isEmptyFetchResult) {
                        return (
                            <div className={classes.emptyResult}>
                                {t('NOTHING_TO_SHOW')}
                            </div>
                        );
                    }

                    return (
                        sortBy(suggestions || [], [sortingCallBack])
                            .map((suggestion, index) => {
                                const label = suggestion.label || suggestion.value || '';
                                return ((typeof renderMenuItem === 'function')
                                    ? renderMenuItem({
                                        suggestion, index, onSelect, selectedItemIndex, classes,
                                    })
                                    : <MenuItem
                                        tabIndex={0}
                                        key={index}
                                        className={classes.option}
                                        focus='true'
                                        selected={selectedItemIndex === index}
                                        data-test-id='menu-item'
                                        onClick={(e) => {
                                            if (isFunction(onSelect)) {
                                                e.preventDefault();
                                                e.stopPropagation();

                                                onSelect(suggestion);
                                            }
                                        }}
                                    >
                                        {
                                            searchValueToHighlight ? <Highlighter
                                                highlightClassName="highlighted-text"
                                                searchWords={[searchValueToHighlight]}
                                                autoEscape={true}
                                                textToHighlight={label}
                                            /> : label
                                        }
                                    </MenuItem>
                                );
                            })
                    );
                })()}
            </Paper>
        </div>
    );
};

SuggestionOptions.propTypes = {
    testId: PropTypes.string,
    classNames: PropTypes.string,
    suggestions: PropTypes.array,
    onSelect: PropTypes.func,
    onClose: PropTypes.func,
    isOpen: PropTypes.bool,
    isFetching: PropTypes.bool,
    isEmptyFetchResult: PropTypes.bool,
    keyDownEvent: PropTypes.object,
    renderMenuItem: PropTypes.func,
    searchValueToHighlight: PropTypes.string,
};

export default memo(SuggestionOptions);
